import { TicketType } from '@superbet-group/betting.lib.payments';
import {
    BetSlip,
    BetSlipError,
    BetSlipRestrictionsConfig,
    Selection,
    SelectionError,
    validateBetSlip,
    validateSelections,
    validateBonusInBetSlip,
    BetSlipBonusRestrictionsConfig,
    Bonus,
    BonusBetSlipError,
    BetSlipErrorType,
} from '@superbet-group/betting.lib.validation';
import { getConfig } from '@superbet-group/web.lib.config';
import { maintenanceService } from '@superbet-group/web.lib.flags';
import {
    MaintenanceType,
    BetSlipPurchaseType,
} from '@superbet-group/web.types';

import BonusEligibility from '@core/models/bonus/BonusEligibility';
import { BonusUtilization } from '@core/models/bonus/BonusUtilization';
import UtilizationEvent from '@core/models/bonus/UtilizationEvent';
import { BetSlipType } from '@core/models/shared/betSlip/betSlipEnums';
import { TaxService } from '@core/services/betSlip/TaxService';
import { BetSlipData } from '@models/betSlip/IBetSlip';
import ISelection from '@models/betSlip/ISelection';
import { Payout } from '@models/betSlip/Payout';
import { isNetWin } from '@shared/betSlip/helpers/betSlipBonusHelper';

import {
    areMarketsAllowed,
    areMaxSelectionsForEvent,
    initAllowances,
} from './allowances';
import type { RestrictionsConfig } from '@superbet-group/betting.lib.validation/dist/selections/types';
import { getSportOfferConfig } from '@sportOfferFederation/config/sportOfferConfig';

const config = getConfig();

export async function validateSportSelections({
    purchaseType,
    betSlipType,
    selections,
    stake,
    isSubmit,
}:{
    purchaseType: BetSlipPurchaseType;
    betSlipType: BetSlipType;
    selections: ISelection[];
    stake: number;
    isSubmit: boolean;
}): Promise<Record<string, SelectionError>> {
    const config: RestrictionsConfig = {
        areMarketsAllowed,
        enabledEventMaintenance: true,
        enabledEventIsFinished: true,
        enabledOddIsFinished: true,
        enabledSelectionIsLocked: true,
        enabledSelectionIsUnavailableForBetting: true,
    };

    config.areMaxSelectionsForEvent = areMaxSelectionsForEvent;

    const numOfSelectionsPerEvent = selections.reduce((acc, selection) => {
        if (!acc[selection.getEventId()]) {
            acc[selection.getEventId()] = 0;
        }

        acc[selection.getEventId()] += 1;

        return acc;
    }, {} as Record<number, number>);

    if (Object.values(numOfSelectionsPerEvent).some((num) => num > 1)) {
        await initAllowances();
    }

    const selectionErrors = validateSelections({
        selections: mapSelections(selections),
        getNetStake: (stake: number) => TaxService.getInstance().getNetStake(stake, purchaseType),
        maintenanceFlags: {
            isPrematchInMaintenance: maintenanceService.isInMaintenance(MaintenanceType.Prematch),
            isLiveInMaintenance: maintenanceService.isInMaintenance(MaintenanceType.Live),
        },
        minStake: getMinStake(betSlipType),
        stake,
        isSubmit,
        config,
    });

    return mapSelectionErrors(selectionErrors);
}

export function validateBonusBetSlip(
    betSlip: BetSlipWeb,
    activeSportBonus: BonusEligibility,
    isOnline: boolean,
    userBalance: number | undefined,
): BonusBetSlipError[] {
    const betSlipBonusConfig: BetSlipBonusRestrictionsConfig = {
        isFreeBet: isNetWin(activeSportBonus),
        timeMarginForEventStart: config.countrySpecific.sport.timeMarginForEventStart,
        hasEligibilityConditions: activeSportBonus.utilization !== BonusUtilization.AnyBet,
        isOnline,
        userBalance,
    };

    return validateBonusInBetSlip(mapBetSlip(betSlip), mapBonus(activeSportBonus), betSlipBonusConfig);
}

export function validateSportBetSlip(
    betSlip: BetSlipWeb,
    activeSportBonus: Nullable<BonusEligibility>,
    isSubmit: boolean,
    rounding: {
        toTwoDecimals: (value: number) => string;
    },
    tempAccount?: boolean,
    userBalance?: number,
    isOnline?: boolean,
): BetSlipError[] {
    const tempAccountMaxWin = (tempAccount && config.countrySpecific.user.tempAccountMaxWin)
        ? config.countrySpecific.user.tempAccountMaxWin
        : undefined;
    const enabledWinLowerThanStakeCheck = !config.countrySpecific.betSlip.validationsConfig.isEnabledWinLowerThanStake
        && (activeSportBonus === null || (activeSportBonus && !isNetWin(activeSportBonus)));

    const betslipConfig: BetSlipRestrictionsConfig = {
        maxStake: config.betSlip.stakeMax,
        minStakeForSimple: config.betSlip.stakeMin,
        minStakeForSystem: config.betSlip.stakeMin,
        minStakePerCombination: config.betSlip.minStakePerCombination,
        maxSelectedNumbers: config.betSlip.maxSelections!,
        maxWin: config.betSlip.maxWin,
        tempAccountMaxWin,
        enabledEmptyBetSlip: true,
        enabledLiveEventsOnOfflineTypeCheck: true,
        enabledNoSelectedSystemCombination: true,
        enabledWinLowerThanStakeCheck,
        enabledSubmitWithMaxWinCheck: !config.countrySpecific.betSlip.allowSubmitWithMaxWin,
        userBalance: activeSportBonus != null ? undefined : userBalance, // We validate stake with bonus via `validateBonusBetSlip`
        isOnline,
    };

    const errors = validateBetSlip(
        mapBetSlip(betSlip),
        betslipConfig,
        rounding,
        isSubmit,
    );

    if (getSportOfferConfig().isPrepareLiveTicketsEnabled) {
        // In this case, we ignore the error about preparing live tickets, the backend allows for it, but we
        // cannot yet change the lib as to not mess up SSBT/RBA which also use this validation
        return errors.filter((error) => error.type !== BetSlipErrorType.LiveEventOnOfflineType);
    }

    return errors;
}

function mapBetSlip(betSlip: BetSlipWeb): BetSlip {
    return {
        stake: betSlip.stake,
        stakePerCombination: betSlip.stakePerCombination,
        type: betSlip.type, // should we remap this
        purchaseType: purchaseTypeToTicketTypeMapper[betSlip.purchaseType],
        selectedSystems: betSlip.selectedSystems.map((system) => ({
            minNumber: system.getMinNumber(),
            fromNumber: system.getFromNumber(),
        })),
        payout: {
            base: betSlip.payout.base,
            final: betSlip.payout.final,
            hasBeenLimited: betSlip.payout.hasBeenLimited,
        },
        selections: mapSelections(betSlip.selections),
        totalCoefficient: betSlip.totalCoefficient,
        combinationsOddSum: betSlip.payout.combinationsOddSum,
    };
}

function mapBonus(activeBonus: BonusEligibility): Bonus {
    return {
        bonusType: activeBonus.winType,
        bonusAmount: activeBonus.balance,
        expirationDate: activeBonus.expirationDate || null,
        eligibleEventIds: getUtilizationEventIds(activeBonus.utilizationEvents),
        minMultipleTicketEvents: activeBonus.minMultipleTicketEvents || null,
        minMultipleTicketOdd: activeBonus.minMultipleTicketOdd || null,
        minEventOddInMultipleTicket: activeBonus.minEventOddInMultipleTicket || null,
        marketOdds: activeBonus.eventsOdds,
        minTicketOdd: activeBonus.minTicketOdd || null,
        minEventOdd: activeBonus.minEventOdd || null,
    };
}

function mapSelections(selections: ISelection[]): Selection[] {
    return selections.map((selection) => ({
        // TODO: now we are ok with eventId as identificator, but we need to refactor selctions to use uuid
        uuid: `${selection.getEventId()}`,
        eventId: selection.getEventId(),
        marketId: selection.getMarketId() || undefined,
        tournamentId: selection.getTournamentId(),
        isEventFinished: selection.isEventFinished(),
        isOngoing: selection.isOngoing(),
        isLocked: selection.isLocked(),
        exists: selection.exists(),
        date: selection.event.date,
        odd: selection.odd,
        tags: selection.getTags(),
        rules: selection.getRules() as Selection['rules'],
        isAvailableForBetting: selection.isAvailableForBetting(),
        bettingPeriod: selection.getBettingPeriod(),
        eventName: selection.getEventFullName(),
    }));
}

function mapSelectionErrors(selectionErrors: SelectionError[]) {
    const result: Record<string, SelectionError> = {};
    selectionErrors.forEach((error) => {
        if (!result[error.uuid]) {
            result[error.uuid] = error;
        }
    });

    return result;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getMinStake(betSlipType: BetSlipType) {
    return 2; // todo this check is obsolete, doesn't make any sense
}

function getUtilizationEventIds(utilizationEvents: (UtilizationEvent | number)[] | undefined): number[] {
    if (utilizationEvents) {
        return isEventArrayNumeric(utilizationEvents)
            ? utilizationEvents as number[]
            : (utilizationEvents as UtilizationEvent[]).map((event) => parseInt(event.id, 10));
    } else {
        return [];
    }
}

function isEventArrayNumeric(array: (UtilizationEvent | number)[]): boolean {
    return array.every((el) => typeof el === 'number');
}

const purchaseTypeToTicketTypeMapper: Record<BetSlipPurchaseType, TicketType> = {
    [BetSlipPurchaseType.offline]: TicketType.retail,
    [BetSlipPurchaseType.online]: TicketType.online,
};

interface BetSlipWeb extends BetSlipData {
    payout: Payout;
    stakePerCombination: number;
}
